import React, { useContext, useState } from "react";
import {
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { updateRfq } from "../../api/ProcurementClient";
import { AppContext } from "../../AppContextProvider";
import { bumpRfqClientEpoch, mergeRfqIntoList } from "../../utils/mergeRfqIntoList";
import { rfqPriorityOption, RFQ_PRIORITY_OPTIONS } from "./rfqPriorityOptions";

interface RfqPrioritySelectorProps {
  rfqId?: number;
  priority: number;
  onPriorityChange?: (priority: number) => void;
  hidden?: boolean;
  disabled?: boolean;
}

const RfqPrioritySelector: React.FC<RfqPrioritySelectorProps> = ({
  rfqId,
  priority,
  onPriorityChange,
  hidden = false,
  disabled = false,
}) => {
  const { setRfqs, setSnackbarMsg, setSnackbarErrorMsg } = useContext(AppContext);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [saving, setSaving] = useState(false);

  if (hidden) {
    return null;
  }

  const current = rfqPriorityOption(priority);

  const handlePriorityChange = async (nextPriority: number) => {
    setMenuAnchor(null);
    if (nextPriority === priority) {
      return;
    }

    if (onPriorityChange) {
      onPriorityChange(nextPriority);
      return;
    }

    if (rfqId == null) {
      return;
    }

    setSaving(true);
    bumpRfqClientEpoch(rfqId);
    setRfqs((prev) =>
      prev.map((rfq) =>
        rfq.id === rfqId ? { ...rfq, priority: nextPriority } : rfq
      )
    );
    try {
      const updated = await updateRfq(rfqId, { priority: nextPriority });
      bumpRfqClientEpoch(rfqId);
      setRfqs((prev) => mergeRfqIntoList(prev, updated));
      setSnackbarMsg(
        `RFQ ${updated.referenceNum} priority set to ${rfqPriorityOption(nextPriority).label}.`,
      );
    } catch (e) {
      bumpRfqClientEpoch(rfqId);
      setRfqs((prev) =>
        prev.map((rfq) =>
          rfq.id === rfqId ? { ...rfq, priority } : rfq
        )
      );
      setSnackbarErrorMsg(
        e instanceof Error ? e.message : "Could not update RFQ priority.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tooltip title={current.label} placement="top">
        <Box
          component="button"
          type="button"
          disabled={saving || disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) {
              return;
            }
            setMenuAnchor(e.currentTarget);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            background: "none",
            padding: 0,
            margin: 0,
            minWidth: 18,
            minHeight: 18,
            cursor: saving || disabled ? "default" : "pointer",
          }}
        >
          <Box sx={current.indicatorSx} />
        </Box>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        {RFQ_PRIORITY_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === priority}
            disabled={saving}
            onClick={() => void handlePriorityChange(option.value)}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Box sx={option.indicatorSx} />
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default RfqPrioritySelector;
